import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { Loader } from '@insio/ui-toolkit';
import { MASTER_ACCOUNT_BOARD_PATH_PREFIX, navigation } from '../navigation';
import { ErrorPage } from '../features/error/pages/ErrorPage.tsx';
import { FeatureFlagGate, FeatureFlagKey } from '@/features/feature-flags';
import { useMasterAccountBridgesEnabled } from '@/features/integrations/hooks/use-master-account-bridges-enabled';
import { MasterAccountBridgesNotFound } from '@/features/integrations/components/bridges/MasterAccountBridgesGate';
// Static: the board layout already loads the master-account-board-shell barrel eagerly.
import { MasterAccountBoardHomeRedirect } from '@/features/master-account-board-shell';
import { LegacyCodeListsRedirect } from '@/components/settings-shell/LegacyCodeListsRedirect';
import { LegacySettingsRedirect } from '@/components/settings-shell/LegacySettingsRedirect';

// Lazy load page components; DashboardRoute too, as it imports the auth barrel, which must not
// load eagerly for routes that never touch it.
const DashboardRoute = lazy(() =>
  import('./DashboardRoute').then(m => ({ default: m.DashboardRoute }))
);
const PasswordlessAuthPage = lazy(() =>
  import('../features/auth/pages/PasswordlessAuthPage').then(m => ({
    default: m.PasswordlessAuthPage
  }))
);
const SignInPage = lazy(() =>
  import('../features/auth/pages/SignInPage').then(m => ({ default: m.SignInPage }))
);
const MagicLinkSentPage = lazy(() =>
  import('../features/auth/pages/MagicLinkSentPage').then(m => ({ default: m.MagicLinkSentPage }))
);
const MagicLinkCallbackPage = lazy(() =>
  import('../features/auth/pages/MagicLinkCallbackPage').then(m => ({
    default: m.MagicLinkCallbackPage
  }))
);
const ForgotPasswordPage = lazy(() =>
  import('../features/auth/pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage }))
);
const VerifyEmailPage = lazy(() =>
  import('../features/auth/pages/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage }))
);
const EmailVerificationPendingPage = lazy(() =>
  import('../features/auth/pages/EmailVerificationPendingPage').then(m => ({
    default: m.EmailVerificationPendingPage
  }))
);
const ImpersonateCallbackPage = lazy(() =>
  import('../features/auth/pages/ImpersonateCallbackPage').then(m => ({
    default: m.ImpersonateCallbackPage
  }))
);
const EmailUnsubscribePage = lazy(() =>
  import('../features/email-unsubscribe/pages/EmailUnsubscribePage').then(m => ({
    default: m.EmailUnsubscribePage
  }))
);
const AcceptInvitationPage = lazy(() =>
  import('../features/auth/pages/AcceptInvitationPage').then(m => ({
    default: m.AcceptInvitationPage
  }))
);
const DocumentsPage = lazy(() =>
  import('../features/documents/pages/DocumentListPage').then(m => ({
    default: m.DocumentListPage
  }))
);
const DocumentDetailPage = lazy(() =>
  import('../features/documents/pages/DocumentDetailPage').then(m => ({
    default: m.DocumentDetailPage
  }))
);
const OpenQuestionsPage = lazy(() =>
  import('../features/document-threads/pages/OpenQuestionsPage').then(m => ({
    default: m.OpenQuestionsPage
  }))
);
const InvoiceListPage = lazy(() =>
  import('../features/invoices/pages/InvoiceListPage').then(m => ({ default: m.InvoiceListPage }))
);
const InvoiceDetailPage = lazy(() =>
  import('../features/invoices/pages/invoice-detail/InvoiceDetailPage.tsx').then(m => ({
    default: m.InvoiceDetailPage
  }))
);
const InvoiceDocumentEditorPage = lazy(() =>
  import('../features/invoices/pages/InvoiceDocumentEditorPage').then(m => ({
    default: m.InvoiceDocumentEditorPage
  }))
);
const ReceiptDetailPage = lazy(() =>
  import('../features/invoices/pages/receipt-detail/ReceiptDetailEditor.tsx').then(m => ({
    default: m.ReceiptDetailEditor
  }))
);

const MyAccountPage = lazy(() =>
  import('../features/profile/pages/MyAccountPage').then(m => ({ default: m.MyAccountPage }))
);
const SettingsPage = lazy(() =>
  import('../features/admin/pages/SettingsPage').then(m => ({
    default: m.SettingsPage
  }))
);
const IntegrationsPage = lazy(() =>
  import('../features/integrations/pages/IntegrationsPage').then(m => ({
    default: m.IntegrationsPage
  }))
);
const ConnectorDetailPage = lazy(() =>
  import('../features/integrations/pages/ConnectorDetailPage').then(m => ({
    default: m.ConnectorDetailPage
  }))
);
const ConnectionsPage = lazy(() =>
  import('../features/integrations/pages/ConnectionsPage').then(m => ({
    default: m.ConnectionsPage
  }))
);
const TargetConnectionPage = lazy(() =>
  import('../features/integrations/pages/TargetConnectionPage').then(m => ({
    default: m.TargetConnectionPage
  }))
);
const IntegrationSetupPage = lazy(() =>
  import('../features/integrations/pages/IntegrationSetupPage').then(m => ({
    default: m.IntegrationSetupPage
  }))
);
const BridgesPage = lazy(() =>
  import('../features/integrations/pages/BridgesPage').then(m => ({ default: m.BridgesPage }))
);
const RegisterBridgePage = lazy(() =>
  import('../features/integrations/pages/RegisterBridgePage').then(m => ({
    default: m.RegisterBridgePage
  }))
);
const BridgeStatusPage = lazy(() =>
  import('../features/integrations/pages/BridgeStatusPage').then(m => ({
    default: m.BridgeStatusPage
  }))
);
const SyncMonitorPage = lazy(() =>
  import('../features/integrations/pages/SyncMonitorPage').then(m => ({
    default: m.SyncMonitorPage
  }))
);
// Fetches the settings page's chunk with the layout's, so a first visit waits for neither in turn.
const SettingsLayout = lazy(() =>
  Promise.all([
    import('../components/settings-shell/SettingsLayout'),
    import('../features/admin/pages/SettingsPage')
  ]).then(([m]) => ({ default: m.SettingsLayout }))
);
const PartnersPage = lazy(() =>
  import('../features/partners/pages/PartnersPage').then(m => ({ default: m.PartnersPage }))
);
const BankMovementsPage = lazy(() =>
  import('../features/bank-movements/pages/BankMovementsPage').then(m => ({
    default: m.BankMovementsPage
  }))
);
const PayablesPage = lazy(() =>
  import('../features/payments/pages/PayablesPage').then(m => ({ default: m.PayablesPage }))
);
const PaymentRunsPage = lazy(() =>
  import('../features/payments/pages/PaymentRunsPage').then(m => ({
    default: m.PaymentRunsPage
  }))
);
const PaymentRunDetailPage = lazy(() =>
  import('../features/payments/pages/PaymentRunDetailPage').then(m => ({
    default: m.PaymentRunDetailPage
  }))
);
const BankStatementImportsPage = lazy(() =>
  import('../features/bank-movements/pages/BankStatementImportsPage').then(m => ({
    default: m.BankStatementImportsPage
  }))
);
const BankMovementQueuePage = lazy(() =>
  import('../features/bank-movements/pages/BankMovementQueuePage').then(m => ({
    default: m.BankMovementQueuePage
  }))
);
const PartyDetailPage = lazy(() =>
  import('../features/partners/pages/PartyDetailPage').then(m => ({ default: m.PartyDetailPage }))
);
const ProcurementPage = lazy(() =>
  import('../features/procurement/pages/ProcurementPage').then(m => ({
    default: m.ProcurementPage
  }))
);
const RequisitionListPage = lazy(() =>
  import('../features/procurement/pages/requisition-list/RequisitionListPage').then(m => ({
    default: m.RequisitionListPage
  }))
);
const PurchaseOrderListPage = lazy(() =>
  import('../features/procurement/pages/purchase-order-list/PurchaseOrderListPage').then(m => ({
    default: m.PurchaseOrderListPage
  }))
);
const GoodsReceiptListPage = lazy(() =>
  import('../features/procurement/pages/goods-receipt-list/GoodsReceiptListPage').then(m => ({
    default: m.GoodsReceiptListPage
  }))
);
const ContractListPage = lazy(() =>
  import('../features/contracts/pages/contract-list/ContractListPage').then(m => ({
    default: m.ContractListPage
  }))
);
const ContractDetailPage = lazy(() =>
  import('../features/contracts/pages/contract-detail/ContractDetailPage').then(m => ({
    default: m.ContractDetailPage
  }))
);
const RequisitionDetailPage = lazy(() =>
  import('../features/procurement/pages/requisition-detail/RequisitionDetailPage').then(m => ({
    default: m.RequisitionDetailPage
  }))
);
const PurchaseOrderDetailPage = lazy(() =>
  import('../features/procurement/pages/purchase-order-detail/PurchaseOrderDetailPage').then(m => ({
    default: m.PurchaseOrderDetailPage
  }))
);
const GoodsReceiptDetailPage = lazy(() =>
  import('../features/procurement/pages/goods-receipt-detail/GoodsReceiptDetailPage').then(m => ({
    default: m.GoodsReceiptDetailPage
  }))
);
const BillingSuccessPage = lazy(() =>
  import('../features/billing/pages/BillingSuccessPage').then(m => ({
    default: m.BillingSuccessPage
  }))
);
const BillingCancelPage = lazy(() =>
  import('../features/billing/pages/BillingCancelPage').then(m => ({
    default: m.BillingCancelPage
  }))
);
const CreditPurchaseReturnPage = lazy(() =>
  import('../features/credits/pages/CreditPurchaseReturnPage').then(m => ({
    default: m.CreditPurchaseReturnPage
  }))
);
const CreditTransactionsPage = lazy(() =>
  import('../features/credits/pages/CreditTransactionsPage').then(m => ({
    default: m.CreditTransactionsPage
  }))
);
const MasterAccountCreditTransactionsPage = lazy(() =>
  import('../features/credits/pages/CreditTransactionsPage').then(m => ({
    default: m.MasterAccountCreditTransactionsPage
  }))
);
const HomePage = lazy(() =>
  import('../features/ai-chat/pages/HomePage').then(m => ({
    default: m.HomePage
  }))
);
const TappeerAiPage = lazy(() =>
  import('../features/ai-chat/pages/TappeerAiPage').then(m => ({
    default: m.TappeerAiPage
  }))
);
// Lazy like the pages around it.
const MasterAccountBoardRouteGuard = lazy(() =>
  import('../features/master-account-board-shell/components/MasterAccountBoardRouteGuard').then(
    m => ({
      default: m.MasterAccountBoardRouteGuard
    })
  )
);
const MasterAccountBoardSettingsRedirect = lazy(() =>
  import('../features/master-account-board-shell/components/MasterAccountBoardSettingsRedirect').then(
    m => ({
      default: m.MasterAccountBoardSettingsRedirect
    })
  )
);
const MasterAccountBoardDeepLinkRoute = lazy(() =>
  import('../features/master-account-board-shell/components/MasterAccountBoardDeepLinkRoute').then(
    m => ({
      default: m.MasterAccountBoardDeepLinkRoute
    })
  )
);
const MasterAccountBoardPeoplePage = lazy(() =>
  import('../features/master-account-board-shell/pages/MasterAccountBoardPeoplePage').then(m => ({
    default: m.MasterAccountBoardPeoplePage
  }))
);
const MasterAccountBoardBillingPage = lazy(() =>
  import('../features/master-account-board-shell/pages/MasterAccountBoardBillingPage').then(m => ({
    default: m.MasterAccountBoardBillingPage
  }))
);
const MasterAccountBoardSettingsPage = lazy(() =>
  import('../features/master-account-board-shell/pages/MasterAccountBoardSettingsPage').then(m => ({
    default: m.MasterAccountBoardSettingsPage
  }))
);
const MasterAccountBoardManagePage = lazy(() =>
  import('../features/master-account-board-shell/pages/MasterAccountBoardManagePage').then(m => ({
    default: m.MasterAccountBoardManagePage
  }))
);
const MasterAccountBoardSettingsLayout = lazy(() =>
  import('../features/master-account-board-shell/pages/MasterAccountBoardSettingsLayout').then(
    m => ({
      default: m.MasterAccountBoardSettingsLayout
    })
  )
);
const MasterAccountBoardSettingsIndex = lazy(() =>
  import('../features/master-account-board-shell/pages/MasterAccountBoardSettingsLayout').then(
    m => ({
      default: m.MasterAccountBoardSettingsIndex
    })
  )
);
const MasterAccountBoardPage = lazy(() =>
  import('../features/master-account-board/pages/MasterAccountBoardPage').then(m => ({
    default: m.MasterAccountBoardPage
  }))
);
const AccountRelationDetailPage = lazy(() =>
  import('../features/master-account-board/pages/AccountRelationDetailPage').then(m => ({
    default: m.AccountRelationDetailPage
  }))
);
const MasterAccountDocumentQueuePage = lazy(() =>
  import('../features/master-account-board/pages/MasterAccountDocumentQueuePage').then(m => ({
    default: m.MasterAccountDocumentQueuePage
  }))
);
const MasterAccountBridgesPage = lazy(() =>
  import('../features/integrations/pages/BridgesPage').then(m => ({
    default: m.MasterAccountBridgesPage
  }))
);
const MasterAccountRegisterBridgePage = lazy(() =>
  import('../features/integrations/pages/RegisterBridgePage').then(m => ({
    default: m.MasterAccountRegisterBridgePage
  }))
);
const MasterAccountBridgePage = lazy(() =>
  import('../features/integrations/pages/BridgeStatusPage').then(m => ({
    default: m.MasterAccountBridgePage
  }))
);
const IntegrationTemplatesPage = lazy(() =>
  import('../features/master-account-integration-templates/pages/IntegrationTemplatesPage').then(
    m => ({ default: m.IntegrationTemplatesPage })
  )
);
const IntegrationTemplateEditorPage = lazy(() =>
  import('../features/master-account-integration-templates/pages/IntegrationTemplateEditorPage').then(
    m => ({ default: m.IntegrationTemplateEditorPage })
  )
);
const IntegrationTemplateApplyPage = lazy(() =>
  import('../features/master-account-integration-templates/pages/IntegrationTemplateApplyPage').then(
    m => ({ default: m.IntegrationTemplateApplyPage })
  )
);
const InboxPage = lazy(() =>
  import('../features/inbox/pages/InboxPage').then(m => ({
    default: m.InboxPage
  }))
);
const PoliciesPage = lazy(() =>
  import('../features/policies/pages/PoliciesPage').then(m => ({
    default: m.PoliciesPage
  }))
);

/** Hides the payment pages behind the payments flag; off means the page does not exist. */
function PaymentsGate({ children }: { children: ReactNode }) {
  return (
    <FeatureFlagGate
      flag={FeatureFlagKey.PAYMENTS}
      pending={<Loader.Page />}
      fallback={<ErrorPage mode='notFound' />}
    >
      {children}
    </FeatureFlagGate>
  );
}

/** Registering an office bridge is where the server enables it; its list and detail are not gated. */
function MasterAccountBridgesRegisterGate({ children }: { children: ReactNode }) {
  const { enabled, loading } = useMasterAccountBridgesEnabled();
  if (loading) {
    return <Loader.Section />;
  }
  return enabled ? <>{children}</> : <MasterAccountBridgesNotFound />;
}

function LegacyIntegrationEditorRedirect() {
  const { id = '' } = useParams<{ id: string }>();
  return <Navigate to={navigation.integrationConnector(id)} replace />;
}

function LegacyBridgeStatusRedirect() {
  const { id = '' } = useParams<{ id: string }>();
  return <Navigate to={navigation.integrationBridge(id)} replace />;
}

/** An old path, with its sub-path, query and fragment, under its new prefix. */
function LegacyPathRedirect({ from, to }: { from: string; to: string }) {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={to + pathname.slice(from.length) + search + hash} replace />;
}

/** The old board paths carry their period, filters and position into the board path. */
function LegacyBoardRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={to + search} replace />;
}

/** Where a board session lands for any path it has no page for: its first permitted section. */
const masterAccountBoardHome = <MasterAccountBoardHomeRedirect />;

/**
 * Routes of the master-account board. A Tappeer record link (e-mail, bookmark) offers the
 * client that holds the record; every other Tappeer path lands on the board home.
 */
export function MasterAccountBoardRoutes() {
  return (
    <Routes>
      {/* A board session is always a master account, so the picker or the board home — never `home`. */}
      <Route
        path={navigation.dashboard()}
        element={<DashboardRoute home={masterAccountBoardHome} />}
      />
      <Route path={navigation.accountRelations()} element={<MasterAccountBoardPage />} />
      <Route
        path={navigation.accountRelation(':clientId')}
        element={<AccountRelationDetailPage />}
      />
      <Route path={navigation.masterAccountQueue()} element={<MasterAccountDocumentQueuePage />} />
      {/* Nastavení kanceláře: one path per section, the pages below draw only their content. */}
      <Route
        path={navigation.masterAccountSettings()}
        element={<MasterAccountBoardSettingsLayout />}
      >
        <Route index element={<MasterAccountBoardSettingsIndex />} />
        <Route
          path={navigation.masterAccountOfficeSettings()}
          element={<MasterAccountBoardSettingsPage />}
        />
        <Route path={navigation.masterAccountPeople()} element={<MasterAccountBoardPeoplePage />} />
        <Route path={navigation.masterAccountManage()} element={<MasterAccountBoardManagePage />} />
        <Route
          path={navigation.masterAccountBilling()}
          element={<MasterAccountBoardBillingPage />}
        />
        <Route
          path={navigation.masterAccountCreditTransactions()}
          element={<MasterAccountCreditTransactionsPage />}
        />
        <Route path={navigation.masterAccountBridges()} element={<MasterAccountBridgesPage />} />
        <Route
          path={navigation.masterAccountBridgeNew()}
          element={
            <MasterAccountBridgesRegisterGate>
              <MasterAccountRegisterBridgePage />
            </MasterAccountBridgesRegisterGate>
          }
        />
        <Route path={navigation.masterAccountBridge(':id')} element={<MasterAccountBridgePage />} />
        <Route
          path={navigation.masterAccountIntegrationTemplates()}
          element={<IntegrationTemplatesPage />}
        />
        <Route
          path={navigation.masterAccountIntegrationTemplateNew()}
          element={<IntegrationTemplateEditorPage />}
        />
        <Route
          path={navigation.masterAccountIntegrationTemplate(':id')}
          element={<IntegrationTemplateEditorPage />}
        />
        <Route
          path={navigation.masterAccountIntegrationTemplateApply(':id')}
          element={<IntegrationTemplateApplyPage />}
        />
        <Route
          path={navigation.masterAccountIntegrationTemplateCopy()}
          element={<IntegrationTemplateApplyPage />}
        />
      </Route>
      <Route
        path={navigation.legacyMasterAccountPeople()}
        element={
          <LegacyPathRedirect
            from={navigation.legacyMasterAccountPeople()}
            to={navigation.masterAccountPeople()}
          />
        }
      />
      <Route
        path={`${navigation.legacyMasterAccountBilling()}/*`}
        element={
          <LegacyPathRedirect
            from={navigation.legacyMasterAccountBilling()}
            to={navigation.masterAccountBilling()}
          />
        }
      />
      <Route
        path={`${navigation.legacyMasterAccountBridges()}/*`}
        element={
          <LegacyPathRedirect
            from={navigation.legacyMasterAccountBridges()}
            to={navigation.masterAccountBridges()}
          />
        }
      />
      <Route path={MASTER_ACCOUNT_BOARD_PATH_PREFIX} element={masterAccountBoardHome} />
      <Route
        path={`${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/*`}
        element={<ErrorPage mode='notFound' />}
      />
      <Route
        path={navigation.legacyMasterAccountBoard()}
        element={<LegacyBoardRedirect to={navigation.accountRelations()} />}
      />
      <Route
        path={navigation.legacyMasterAccountQueue()}
        element={<LegacyBoardRedirect to={navigation.masterAccountQueue()} />}
      />
      {/* Deep-linkable Tappeer records (see the deep-link module): offer the owning client. */}
      <Route
        path={navigation.documentDetail(':id')}
        element={<MasterAccountBoardDeepLinkRoute />}
      />
      <Route path={navigation.invoiceDetail(':id')} element={<MasterAccountBoardDeepLinkRoute />} />
      <Route
        path={navigation.invoiceScanEditor(':id')}
        element={<MasterAccountBoardDeepLinkRoute />}
      />
      <Route path={navigation.receiptDetail(':id')} element={<MasterAccountBoardDeepLinkRoute />} />
      <Route
        path={navigation.legacySystemSettings()}
        element={<MasterAccountBoardSettingsRedirect />}
      />
      <Route path={navigation.settings()} element={<MasterAccountBoardSettingsRedirect />} />
      <Route
        path={`${navigation.settings()}/:section`}
        element={<MasterAccountBoardSettingsRedirect />}
      />
      <Route path={navigation.myAccount()} element={<MyAccountPage />} />
      <Route path={navigation.billingSuccess()} element={<BillingSuccessPage />} />
      <Route path={navigation.billingCancel()} element={<BillingCancelPage />} />
      <Route path={navigation.creditPurchaseReturn()} element={<CreditPurchaseReturnPage />} />
      <Route path={navigation.policies()} element={<PoliciesPage />} />
      <Route
        path={navigation.termsOfService()}
        element={<Navigate to={navigation.policies('terms')} replace />}
      />
      <Route path='*' element={masterAccountBoardHome} />
    </Routes>
  );
}

/** Routes of the Tappeer app — every account that is not a master account board, and public pages. */
export function TappeerRoutes() {
  return (
    <Routes>
      <Route path={navigation.dashboard()} element={<DashboardRoute home={<HomePage />} />} />
      {/* The assistant by URL (e-mail links): on a phone too, and while onboarding is pending. */}
      <Route path={navigation.tappeerAi()} element={<TappeerAiPage />} />
      <Route
        path={navigation.legacyMasterAccountBoard()}
        element={<LegacyBoardRedirect to={navigation.accountRelations()} />}
      />
      <Route
        path={navigation.legacyMasterAccountQueue()}
        element={<LegacyBoardRedirect to={navigation.masterAccountQueue()} />}
      />
      {/* Browser Back after entering a client lands on a board path with the client's
          session, where no board query can resolve. */}
      <Route path={MASTER_ACCOUNT_BOARD_PATH_PREFIX} element={<MasterAccountBoardRouteGuard />} />
      <Route
        path={`${MASTER_ACCOUNT_BOARD_PATH_PREFIX}/*`}
        element={<MasterAccountBoardRouteGuard />}
      />
      <Route path={navigation.signIn()} element={<PasswordlessAuthPage />} />
      <Route path={navigation.passwordSignIn()} element={<SignInPage />} />
      <Route path={navigation.signUp()} element={<PasswordlessAuthPage />} />
      <Route path={navigation.magicLinkSent()} element={<MagicLinkSentPage />} />
      <Route path={navigation.magicLinkCallback()} element={<MagicLinkCallbackPage />} />
      <Route path={navigation.forgotPassword()} element={<ForgotPasswordPage />} />
      <Route path={navigation.verifyEmail()} element={<VerifyEmailPage />} />
      <Route path={navigation.verificationPending()} element={<EmailVerificationPendingPage />} />
      <Route path={navigation.acceptInvitation()} element={<AcceptInvitationPage />} />
      <Route path={navigation.impersonateCallback()} element={<ImpersonateCallbackPage />} />
      <Route path={navigation.unsubscribe()} element={<EmailUnsubscribePage />} />
      <Route path={navigation.documents()} element={<DocumentsPage />} />
      <Route path={navigation.documentDetail(':id')} element={<DocumentDetailPage />} />
      <Route path={navigation.openQuestions()} element={<OpenQuestionsPage />} />
      <Route path={navigation.invoices()} element={<InvoiceListPage />} />
      <Route path={navigation.invoiceDetail(':id')} element={<InvoiceDetailPage />} />
      <Route path={navigation.invoiceScanEditor(':id')} element={<InvoiceDocumentEditorPage />} />
      <Route path={navigation.receiptDetail(':id')} element={<ReceiptDetailPage />} />
      <Route path={navigation.notifications()} element={<InboxPage />} />
      <Route path={navigation.myAccount()} element={<MyAccountPage />} />
      {/* System settings: one layout draws the shell, so it stays mounted from page to page. Its
          section, sub-item and header come from the URL; Firmy and Integrace are sections too. */}
      <Route element={<SettingsLayout />}>
        <Route path={navigation.settings()} element={<SettingsPage />} />
        <Route path={`${navigation.settings()}/*`} element={<SettingsPage />} />
        <Route path={navigation.integrations()} element={<IntegrationsPage />} />
        <Route path={navigation.integrationConnections()} element={<ConnectionsPage />} />
        <Route path={navigation.integrationBridges()} element={<BridgesPage />} />
        <Route path={navigation.integrationMonitor()} element={<SyncMonitorPage />} />
        <Route path={navigation.integrationConnector(':id')} element={<ConnectorDetailPage />} />
        <Route path={navigation.integrationSetup()} element={<IntegrationSetupPage />} />
        <Route path={navigation.integrationConnection(':id')} element={<TargetConnectionPage />} />
        <Route path={navigation.integrationBridgeNew()} element={<RegisterBridgePage />} />
        <Route path={navigation.integrationBridge(':id')} element={<BridgeStatusPage />} />
        <Route path={navigation.partners()} element={<PartnersPage />} />
        <Route path={navigation.partnerDetail(':id')} element={<PartyDetailPage />} />
      </Route>
      {/* A path below Integrace or Firmy that none of their routes serves goes to the overview
          quietly, outside the layout and its access check. */}
      <Route
        path={`${navigation.integrations()}/*`}
        element={<Navigate to={navigation.settings()} replace />}
      />
      <Route
        path={`${navigation.partners()}/*`}
        element={<Navigate to={navigation.settings()} replace />}
      />
      <Route path={navigation.legacySystemSettings()} element={<LegacySettingsRedirect />} />
      <Route path={navigation.creditTransactions()} element={<CreditTransactionsPage />} />
      <Route
        path={navigation.bankMovements()}
        element={
          <PaymentsGate>
            <BankMovementsPage />
          </PaymentsGate>
        }
      />
      <Route
        path={navigation.bankStatementImports()}
        element={
          <PaymentsGate>
            <BankStatementImportsPage />
          </PaymentsGate>
        }
      />
      <Route
        path={navigation.bankMovementQueue()}
        element={
          <PaymentsGate>
            <BankMovementQueuePage />
          </PaymentsGate>
        }
      />
      <Route
        path={navigation.payments()}
        element={<Navigate to={navigation.payables()} replace />}
      />
      <Route
        path={navigation.payables()}
        element={
          <PaymentsGate>
            <PayablesPage />
          </PaymentsGate>
        }
      />
      <Route
        path={navigation.paymentRuns()}
        element={
          <PaymentsGate>
            <PaymentRunsPage />
          </PaymentsGate>
        }
      />
      <Route
        path={navigation.paymentRunDetail(':id')}
        element={
          <PaymentsGate>
            <PaymentRunDetailPage />
          </PaymentsGate>
        }
      />
      <Route
        path={navigation.legacyIntegrationEditor(':id')}
        element={<LegacyIntegrationEditorRedirect />}
      />
      <Route path={navigation.legacyBridgeStatus(':id')} element={<LegacyBridgeStatusRedirect />} />
      <Route
        path={`${navigation.legacyIntegrations()}/*`}
        element={
          <LegacyPathRedirect
            from={navigation.legacyIntegrations()}
            to={navigation.integrations()}
          />
        }
      />
      <Route
        path={`${navigation.legacyPartners()}/*`}
        element={
          <LegacyPathRedirect from={navigation.legacyPartners()} to={navigation.partners()} />
        }
      />
      <Route path={navigation.legacyCodeLists()} element={<LegacyCodeListsRedirect />} />
      <Route
        path={navigation.procurement()}
        element={
          <FeatureFlagGate
            flag={FeatureFlagKey.PROCUREMENT}
            pending={<Loader.Page />}
            fallback={<ErrorPage mode='notFound' />}
          >
            <ProcurementPage />
          </FeatureFlagGate>
        }
      />
      <Route
        path={navigation.requisitions()}
        element={
          <FeatureFlagGate
            flag={FeatureFlagKey.PROCUREMENT}
            pending={<Loader.Page />}
            fallback={<ErrorPage mode='notFound' />}
          >
            <RequisitionListPage />
          </FeatureFlagGate>
        }
      />
      <Route
        path={navigation.purchaseOrders()}
        element={
          <FeatureFlagGate
            flag={FeatureFlagKey.PROCUREMENT}
            pending={<Loader.Page />}
            fallback={<ErrorPage mode='notFound' />}
          >
            <PurchaseOrderListPage />
          </FeatureFlagGate>
        }
      />
      <Route
        path={navigation.goodsReceipts()}
        element={
          <FeatureFlagGate
            flag={FeatureFlagKey.PROCUREMENT}
            pending={<Loader.Page />}
            fallback={<ErrorPage mode='notFound' />}
          >
            <GoodsReceiptListPage />
          </FeatureFlagGate>
        }
      />
      {/* Outside /procurement: contracts follow their own flag. */}
      <Route
        path={navigation.contracts()}
        element={
          <FeatureFlagGate
            flag={FeatureFlagKey.CONTRACTS}
            pending={<Loader.Page />}
            fallback={<ErrorPage mode='notFound' />}
          >
            <ContractListPage />
          </FeatureFlagGate>
        }
      />
      <Route
        path={navigation.contractDetail(':id')}
        element={
          <FeatureFlagGate
            flag={FeatureFlagKey.CONTRACTS}
            pending={<Loader.Page />}
            fallback={<ErrorPage mode='notFound' />}
          >
            <ContractDetailPage />
          </FeatureFlagGate>
        }
      />
      <Route
        path={navigation.requisitionDetail(':id')}
        element={
          <FeatureFlagGate
            flag={FeatureFlagKey.PROCUREMENT}
            pending={<Loader.Page />}
            fallback={<ErrorPage mode='notFound' />}
          >
            <RequisitionDetailPage />
          </FeatureFlagGate>
        }
      />
      <Route
        path={navigation.purchaseOrderDetail(':id')}
        element={
          <FeatureFlagGate
            flag={FeatureFlagKey.PROCUREMENT}
            pending={<Loader.Page />}
            fallback={<ErrorPage mode='notFound' />}
          >
            <PurchaseOrderDetailPage />
          </FeatureFlagGate>
        }
      />
      <Route
        path={navigation.goodsReceiptDetail(':id')}
        element={
          <FeatureFlagGate
            flag={FeatureFlagKey.PROCUREMENT}
            pending={<Loader.Page />}
            fallback={<ErrorPage mode='notFound' />}
          >
            <GoodsReceiptDetailPage />
          </FeatureFlagGate>
        }
      />
      <Route path={navigation.billingSuccess()} element={<BillingSuccessPage />} />
      <Route path={navigation.billingCancel()} element={<BillingCancelPage />} />
      <Route path={navigation.creditPurchaseReturn()} element={<CreditPurchaseReturnPage />} />
      <Route path={navigation.policies()} element={<PoliciesPage />} />
      <Route
        path={navigation.termsOfService()}
        element={<Navigate to={navigation.policies('terms')} replace />}
      />
      {/* Catch-all route now uses unified ErrorPage */}
      <Route path='*' element={<ErrorPage mode='notFound' />} />
    </Routes>
  );
}

interface AppRouterProps {
  /** `masterAccountBoard` for a master account account's session; everything else is the Tappeer app. */
  mode?: 'tappeer' | 'masterAccountBoard';
}

export function AppRouter({ mode = 'tappeer' }: AppRouterProps) {
  return (
    <Suspense fallback={<Loader.Page />}>
      {mode === 'masterAccountBoard' ? <MasterAccountBoardRoutes /> : <TappeerRoutes />}
    </Suspense>
  );
}

export default AppRouter;
